import { ColumnDef } from '@tanstack/react-table'
import { CantileverButton } from 'components/CantileverButton'
import { TextInput } from 'components/form/inputs/TextInput'
import { TextCell } from 'components/table/cells/TextCell'
import { ColorSwatch } from 'features/timeSeries/components/ColorSwatch'
import { X } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'

const RemoveButtonCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledX = styled(X)`
  height: 1rem;
  width: 1rem;
  color: ${({ theme }) => theme.forecast.danger};
`

const RemoveHeaderCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  position: relative;

  input:disabled {
    color: ${({ theme }) => theme.forecast.defaultText};
    opacity: 1;
    -webkit-text-fill-color: ${({ theme }) => theme.forecast.defaultText};
    cursor: not-allowed;
  }
`

interface EditableNumberCellProps {
  value: number
  sensorId: string
  placeholder: string
  onChange: (sensorId: string, value: string) => void
  disabled?: boolean
}

const EditableNumberCell: React.FC<EditableNumberCellProps> = ({
  value,
  sensorId,
  placeholder,
  onChange,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState<string>(value.toString())

  useEffect(() => {
    setLocalValue(value.toString())
  }, [value])

  const validateAndUpdate = (inputValue: string) => {
    if (disabled) return
    if (inputValue === '') {
      onChange(sensorId, inputValue)
      return
    }
    const parsed = parseFloat(inputValue)
    if (!isNaN(parsed) && isFinite(parsed)) {
      onChange(sensorId, inputValue)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      setLocalValue(e.target.value)
    }
  }

  const handleBlur = () => {
    validateAndUpdate(localValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateAndUpdate(localValue)
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      setLocalValue(value.toString())
      e.currentTarget.blur()
    }
  }

  const handleDoubleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (!disabled) {
      e.currentTarget.select()
    }
  }

  const handleClear = () => {
    if (!disabled) {
      setLocalValue('')
    }
  }

  return (
    <InputWrapper>
      <TextInput
        type='input'
        value={localValue}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onDoubleClick={handleDoubleClick}
        mode='edit'
        disabled={disabled}
      />
      {localValue && !disabled && (
        <CantileverButton iconOnly ghost onClick={handleClear}>
          <StyledX />
        </CantileverButton>
      )}
    </InputWrapper>
  )
}

export const SensorScatterplotTableColumns = (
  onRemove: (itemId: string, isDerived?: boolean) => void,
  onRemoveAll: (event: React.MouseEvent<HTMLButtonElement>) => void,
  onYMinChange: (sensorId: string, value: string) => void,
  onYMaxChange: (sensorId: string, value: string) => void,
  sensorRanges: Record<string, { min: number; max: number; unit: string }>,
  onChangeColor: (sensorId: string, color: string) => void,
  autoScaleMode: boolean
): ColumnDef<SensorScatterplotData>[] => {
  return useMemo(
    () => [
      {
        id: 'remove',
        header: () => (
          <RemoveHeaderCell>
            <CantileverButton iconOnly onClick={(e) => onRemoveAll(e)}>
              <StyledX />
            </CantileverButton>
          </RemoveHeaderCell>
        ),
        cell: ({ row }) => (
          <RemoveButtonCell>
            <CantileverButton
              iconOnly
              onClick={() => onRemove(row.original.id, row.original.isDerived)}>
              <StyledX />
            </CantileverButton>
          </RemoveButtonCell>
        ),
      },
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <TextCell
            truncate
            maxWidth='8rem'
            text={row.original.name}
            subtitle={row.original.description}
          />
        ),
      },
      {
        accessorKey: 'yMin',
        id: 'yMin',
        header: 'Y Min',
        cell: ({ row }) => {
          return (
            <EditableNumberCell
              value={row.original.yMin}
              sensorId={row.original.id}
              placeholder={sensorRanges[row.original.id]?.min?.toString() || ''}
              onChange={onYMinChange}
              disabled={autoScaleMode}
            />
          )
        },
      },
      {
        accessorKey: 'yMax',
        id: 'yMax',
        header: 'Y Max',
        cell: ({ row }) => {
          return (
            <EditableNumberCell
              value={row.original.yMax}
              sensorId={row.original.id}
              placeholder={sensorRanges[row.original.id]?.max?.toString() || ''}
              onChange={onYMaxChange}
              disabled={autoScaleMode}
            />
          )
        },
      },
      {
        accessorKey: 'color',
        id: 'color',
        header: 'Color',
        cell: ({ row }) => (
          <ColorSwatch
            color={row.original.color}
            onChangeColor={(color) => onChangeColor(row.original.id, color)}
          />
        ),
      },
    ],
    [
      onRemove,
      onRemoveAll,
      onYMinChange,
      onYMaxChange,
      sensorRanges,
      onChangeColor,
      autoScaleMode,
    ]
  )
}
